/**
 * Reservation pricing-preview HTTP request schema — SCRUM-223 (DS-012 Reservation BC §9.1).
 *
 * Wire source of truth for `POST /organizations/:slug/reservations/preview`. The
 * controller (apps/api booking interface) reads this via its DTO `static schema`,
 * so the global `ZodValidationPipe` rejects a malformed body with 422 before the
 * handler runs. Shapes mirror the already-typed `CalculateReservationQuoteQueryInput`
 * (SCRUM-222) MINUS the handler-forced fields: `stayKind` (always `'PayingGuest'`
 * in Phase 4), `quoteAt` (Clock-sourced), and the reservation-level distribution
 * override (Phase-6 hook, `null`) are NOT part of the wire contract — the handler
 * owns them so the request cannot express a non-Phase-4 stay (§9.1).
 *
 * Validation boundary (§7.7 two-channel split): this schema owns PER-FIELD
 * structural validity only — a real calendar date (`LocalDateSchema`), integer
 * count ranges, a UUID property id, non-empty optional text. The CROSS-FIELD stay
 * invariants (`checkOutDate > checkInDate`, `nights <= MAX_STAY_NIGHTS`) are deliberately NOT
 * here: they belong to the domain (`StayContext.create`), which surfaces them as
 * `InvalidStayContextError` / `MaxNightsExceededError` → HTTP 400. Encoding them
 * here too would give one error class two conflicting status codes.
 *
 * `.strict()`: an unknown key is a malformed body → 422, never silently ignored.
 */
import { z } from 'zod';
import { LocalDateSchema } from '../time/index.js';
/** Optional free-text discriminator — trimmed non-empty when present; absent or
 * `null` normalises to `null` (the handler input is `string | null`, never
 * `undefined`). An empty/blank string when present is a 422 here, so the domain's
 * non-empty-when-present rule is never the one that fires. */
const optionalDiscriminator = z.string().trim().min(1).nullable().default(null);
export const reservationPreviewSchema = z
    .object({
    // UUID, not merely non-empty: `PropertyId` is a branded UUID everywhere
    // (pricing validators use `z.string().uuid()`); a malformed id is a clean
    // 422 here rather than leaking into the capacity port as a phantom lookup.
    propertyId: z.string().uuid(),
    checkInDate: LocalDateSchema,
    checkOutDate: LocalDateSchema, // exclusive (morning of departure)
    guestCount: z.number().int().min(1),
    petCount: z.number().int().min(0).default(0),
    vehicleCount: z.number().int().min(0).default(0),
    /**
     * SCRUM-337 B1 occupancy seam — subset of `guestCount` that are children
     * (int >= 0, defaults to 0). Tracked/stored only; pricing is invariant to
     * the adults/children split (the calculator keys off total `guestCount`).
     */
    childCount: z.number().int().min(0).default(0),
    reservationType: optionalDiscriminator,
    bookingChannel: optionalDiscriminator,
})
    .strict();
//# sourceMappingURL=reservation-preview.schema.js.map